import { Suspense } from "react";
import { format, parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { EcosuiteErrorBoundary } from "@/components/error-boundary";
import {
  Building2,
  Globe,
  Link,
  User,
  CircleCheck,
  CalendarDays,
} from "lucide-react";
import { STATUS_BADGE_STYLES } from "../../constants/status-badges";
import { cn } from "@/lib/utils";

interface OrganizationInfoCardProps {
  organization: {
    name: string;
    domain: string;
    slug: string;
    responsiblePerson: string;
    status: string;
    createdAt: string | Date;
  };
}

function OrganizationInfoCardContent({
  organization,
}: OrganizationInfoCardProps) {
  return (
    <Card className="border-muted/50 overflow-hidden shadow-sm">
      <CardHeader className="bg-muted/10 pb-2">
        <div className="flex items-center space-x-2">
          <Building2 className="text-primary h-5 w-5" />
          <CardTitle className="text-base">Organization Information</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 pt-4 md:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <div className="flex items-center space-x-1.5">
            <Building2 className="text-muted-foreground h-3.5 w-3.5" />
            <h3 className="text-muted-foreground text-xs font-medium">Name</h3>
          </div>
          <p className="text-sm font-medium">{organization.name}</p>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center space-x-1.5">
            <Globe className="text-muted-foreground h-3.5 w-3.5" />
            <h3 className="text-muted-foreground text-xs font-medium">
              Domain
            </h3>
          </div>
          <p className="text-sm font-medium">{organization.domain}</p>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center space-x-1.5">
            <Link className="text-muted-foreground h-3.5 w-3.5" />
            <h3 className="text-muted-foreground text-xs font-medium">Slug</h3>
          </div>
          <p className="text-sm font-medium">{organization.slug}</p>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center space-x-1.5">
            <User className="text-muted-foreground h-3.5 w-3.5" />
            <h3 className="text-muted-foreground text-xs font-medium">
              Responsible Person
            </h3>
          </div>
          <p className="text-sm font-medium">
            {organization.responsiblePerson}
          </p>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center space-x-1.5">
            <CircleCheck className="text-muted-foreground h-3.5 w-3.5" />
            <h3 className="text-muted-foreground text-xs font-medium">
              Status
            </h3>
          </div>
          <p className="text-sm font-medium">
            <Badge
              variant="outline"
              className={cn(
                "capitalize",
                STATUS_BADGE_STYLES[organization.status] ??
                  STATUS_BADGE_STYLES["NEW"],
              )}
            >
              {organization.status}
            </Badge>
          </p>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center space-x-1.5">
            <CalendarDays className="text-muted-foreground h-3.5 w-3.5" />
            <h3 className="text-muted-foreground text-xs font-medium">
              Created
            </h3>
          </div>
          <p className="text-sm font-medium">
            {format(parseISO(organization.createdAt as string), "PPP")}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export function OrganizationInfoCard(props: OrganizationInfoCardProps) {
  return (
    <EcosuiteErrorBoundary>
      <Suspense
        fallback={
          <LoadingScreen text="Loading organization info..." className="h-64" />
        }
      >
        <OrganizationInfoCardContent {...props} />
      </Suspense>
    </EcosuiteErrorBoundary>
  );
}
